import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { usePortal } from '../contexts/PortalContext';
import { ClientShell } from '../components/ClientShell';
import { ProjectPage } from './client/ProjectPage';
import { EpisodeReview } from './client/EpisodeReview';
import { Transcript } from './client/Transcript';
import { ShowHome } from './client/ShowHome';
import { ShowDeliverables } from './client/ShowDeliverables';

/**
 * 26 Sep 2026 — the client link is one page. The four old tab routes
 * (project, episodes, files, schedule) resolve to it rather than being
 * deleted, because back links across the app still aim at them and a
 * redirect cannot strand anybody. `episode` and `transcript` are real
 * destinations and stay.
 *
 * 8 OCT 2026 — THAT ONE PAGE IS NOW `ProjectPage`, four blocks with one
 * thing leading (see pages/client/ProjectPage.tsx). `Overview` — the
 * twelve-section page it replaces — is still on disk, untouched and
 * unreferenced, because it is a single file larger than one write to this
 * artifact can carry: overwritten, it could not be put back.
 *
 * **TO REVERT THE WHOLE REDESIGN:** change the import above back to
 * `import { Overview } from './client/Overview'` and `<ProjectPage />`
 * below to `<Overview />`. Nothing else in the app points at either.
 */

/**
 * What a client link says when it cannot load.
 *
 * **THESE MUST NOT ASSERT A CAUSE THEY HAVE NOT ESTABLISHED (28 Sep 2026).**
 * `fetchProjectById` returns null on *any* failure, so `missing` is reached
 * by a dead session, a refused permission or an unreachable database just as
 * readily as by a wrong link. The old copy said "check the link Portal sent
 * you", which sent a real investigation to the link, then the email, then
 * the client's firewall — while the actual fault was the app signing the
 * client out. See claude/portal-os-client-signed-out-2026-09-28.md.
 *
 * So: say what we know, offer the action that fixes most of these, and never
 * blame the link unless the database actually rejected the token, which is
 * the one case `denied` does establish.
 */
const LOAD_MESSAGE: Record<string, {
  title: string;
  body: string;
  retry: boolean;
}> = {
  missing: {
    title: 'This page did not open',
    body: 'Something went wrong at our end rather than yours. Trying again usually clears it.',
    retry: true
  },
  denied: {
    title: 'This link is no longer valid',
    body: 'Reply to the email this link came in and Portal will send you a new one.',
    retry: false
  },
  failed: {
    title: 'This page did not open',
    body: 'We could not reach Portal just now. Trying again usually clears it.',
    retry: true
  }
};

/** Routes that used to be their own screen and are now part of the one page. */
const FOLDED_IN = ['home', 'project', 'episodes', 'files', 'schedule'];
export function ClientApp() {
  const {
    clientRoute,
    clientLoad
  } = usePortal();

  /* The very first thing a client sees should never fade in from nothing —
     against the ink-filled card, a 0→1 opacity tween reads as a flash of the
     wrong colour rather than a considered transition. Only route changes
     AFTER that first paint get the fade. */
  const hasPaintedOnce = useRef(false);
  const isFirstPaint = !hasPaintedOnce.current;
  useEffect(() => {
    hasPaintedOnce.current = true;
  });
  if (clientLoad === 'loading') {
    return <ClientShell data-id="element-2228" data-mp="1m986uxcrmpvp">
        <p className="label m-0 text-mute" data-id="element-2229" data-mp="1m9qculp8cigt">Loading your project…</p>
      </ClientShell>;
  }
  const problem = LOAD_MESSAGE[clientLoad];
  if (problem) {
    return <ClientShell data-id="element-2230" data-mp="1vyroy0snmfpi">
        <p className="label m-0 text-mute" data-id="element-2231" data-mp="1vy89qrue25f6">{problem.title}</p>
        <p className="mt-5 max-w-lg text-lg leading-relaxed" data-id="element-2232" data-mp="1w0unxabsbj6d">{problem.body}</p>

        {problem.retry && <button type="button" onClick={() => window.location.reload()} className="label mt-9 border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-2233" data-mp="25wxr8jidhnuw">
            Try again
          </button>}

        <p className="label-sm mt-9 max-w-lg leading-relaxed text-mute" data-id="element-2234" data-mp="37l2swdrtn3wi">
          If it keeps happening, reply to the email this link came in and quote
          reference {clientLoad.toUpperCase()} — it tells Portal exactly where this stopped.
        </p>
      </ClientShell>;
  }

  /* One page per project, so the fade keys on the destination rather than on
     which of the folded-in names got us there. */
  const folded = FOLDED_IN.includes(clientRoute);
  const key = folded ? 'overview' : clientRoute;
  return <ClientShell data-id="element-2235" data-mp="2xozo105l4r5v">
      <motion.div key={key} initial={isFirstPaint ? false : {
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-2236" data-mp="2xpht8ma86fpx">
        {clientRoute === 'show' && <ShowHome data-id="element-2237" data-mp="d13loj26ejt3" />}
        {clientRoute === 'show-deliverables' && <ShowDeliverables data-id="element-2238" data-mp="7yw3vcuo09iu" />}
        {folded && <ProjectPage data-id="element-2239" data-mp="820g8xiaabkl" />}
        {clientRoute === 'episode' && <EpisodeReview data-id="element-2240" data-mp="ggojzjmud4s5" />}
        {clientRoute === 'transcript' && <Transcript data-id="element-2241" data-mp="gjaqrh1wrg9b" />}
      </motion.div>
    </ClientShell>;
}